// ============================================================================
// AnswerSources — блок «Источники» под ответом личного ассистента.
//
// Показывает только фактические источники сервера: прочитанные записи (со
// ссылкой на форму), таблицы, файлы, внешние страницы и инструменты. Если
// сервер не сообщил источников — явное «Источники недоступны», без догадок.
// ============================================================================

import { useMemo, useState } from 'react';
import { ChevronDown, Database, ExternalLink, FileText, Paperclip, Wrench } from 'lucide-react';
import { FilePreviewModal } from '@/components/viewer/FilePreviewModal';
import { openObjectRecord } from '@/lib/chatNavigation';
import type { AnswerSource, AnswerSourceKind } from '@/lib/assistantSources';

interface AnswerSourcesProps {
  sources: AnswerSource[] | null;
  /** Ответ бота-ассистента: без данных показываем явное «недоступны». */
  assistantAnswer?: boolean;
}

const KIND_ORDER: Record<AnswerSourceKind, number> = { record: 0, file: 1, table: 2, url: 3, tool: 4 };
const LINKABLE_TYPES = new Set(['catalog', 'document', 'task', 'order']);

function sourceIcon(kind: AnswerSourceKind) {
  const className = 'h-3 w-3 shrink-0 text-muted-foreground';
  switch (kind) {
    case 'record': return <FileText className={className} />;
    case 'table': return <Database className={className} />;
    case 'file': return <Paperclip className={className} />;
    case 'url': return <ExternalLink className={className} />;
    default: return <Wrench className={className} />;
  }
}

function safeExternalUrl(url: string | undefined): string {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.toString() : '';
  } catch {
    return '';
  }
}

export function AnswerSources({ sources, assistantAnswer = false }: AnswerSourcesProps) {
  const [open, setOpen] = useState(true);
  const [preview, setPreview] = useState<{ fileId: string; fileName: string } | null>(null);

  const ordered = useMemo(
    () => (sources ? [...sources].sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind]) : []),
    [sources],
  );

  if (sources === null && !assistantAnswer) return null;

  const empty = ordered.length === 0;
  return (
    <div
      className="mt-2 min-w-0 rounded-lg border px-2 py-1.5 text-[10.5px] leading-[14px]"
      style={{ borderColor: 'var(--color-border)' }}
      data-assistant-sources
    >
      <button
        type="button"
        className="flex w-full items-center gap-1.5 text-left font-medium text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <span>Источники{empty ? '' : ` (${ordered.length})`}</span>
        <ChevronDown className={`ml-auto h-3 w-3 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>

      {open && (
        empty ? (
          <div className="mt-1 text-muted-foreground">
            Источники недоступны: сервер не сообщил, на какие данные опирался ответ.
          </div>
        ) : (
          <ul className="mt-1 space-y-0.5">
            {ordered.map((source, index) => {
              const recordLink = source.kind === 'record'
                && source.entityType && source.entityCode && source.recordId
                && LINKABLE_TYPES.has(source.entityType)
                ? { type: source.entityType, code: source.entityCode, id: source.recordId }
                : null;
              const fileLink = source.kind === 'file' && source.fileId
                ? { fileId: source.fileId, fileName: source.label }
                : null;
              const external = source.kind === 'url' ? safeExternalUrl(source.url) : '';
              const content = (
                <>
                  {sourceIcon(source.kind)}
                  <span className="min-w-0 flex-1 break-words">
                    <span className="font-medium">{source.label}</span>
                    {source.detail && <span className="text-muted-foreground"> · {source.detail}</span>}
                  </span>
                  {(recordLink || external) && <ExternalLink className="mt-0.5 h-2.5 w-2.5 shrink-0 opacity-60" />}
                </>
              );
              const rowKey = `${source.kind}:${source.table || ''}:${source.recordId || ''}:${source.fileId || ''}:${source.url || ''}:${source.label}:${index}`;
              if (recordLink) {
                return (
                  <li key={rowKey} className="min-w-0">
                    <button
                      type="button"
                      className="flex w-full min-w-0 items-start gap-1.5 text-left hover:text-primary transition-colors"
                      onClick={() => openObjectRecord(recordLink.type, recordLink.code, recordLink.id)}
                      title="Открыть запись"
                    >
                      {content}
                    </button>
                  </li>
                );
              }
              if (fileLink) {
                return (
                  <li key={rowKey} className="min-w-0">
                    <button
                      type="button"
                      className="flex w-full min-w-0 items-start gap-1.5 text-left hover:text-primary transition-colors"
                      onClick={() => setPreview(fileLink)}
                      title="Открыть файл"
                    >
                      {content}
                    </button>
                  </li>
                );
              }
              if (external) {
                return (
                  <li key={rowKey} className="min-w-0">
                    <button
                      type="button"
                      className="flex w-full min-w-0 items-start gap-1.5 text-left hover:text-primary transition-colors"
                      onClick={() => window.open(external, '_blank', 'noopener,noreferrer')}
                      title={source.url}
                    >
                      {content}
                    </button>
                  </li>
                );
              }
              return (
                <li key={rowKey} className="flex min-w-0 items-start gap-1.5">
                  {content}
                </li>
              );
            })}
          </ul>
        )
      )}

      {preview && (
        <FilePreviewModal
          fileId={preview.fileId}
          fileName={preview.fileName}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}
